/**
 * Secret detail page. Metadata-only — the plaintext is never accessible here.
 * Operators can:
 *
 *  - View metadata (name, scope, kind, fingerprint, public key for SSH).
 *  - Rename and edit metadata.
 *  - Copy the public key (when it's an SSH secret).
 *  - Delete.
 *
 * The "rotate" / "replace plaintext" flow is intentionally absent in this
 * round. Rotating means: create a new secret, point everything that
 * referenced the old one at the new one, delete the old. A dedicated
 * rotation UI lands later; for now the plain "delete + create new" path is
 * the explicit version.
 */
import {
  KeyRound,
  Loader2,
  Pencil,
  Trash2,
  Save,
  X,
  ChevronLeft,
  Copy,
  ShieldCheck,
  Info,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/Card.js';
import { Button } from '@/components/ui/Button.js';
import { Input } from '@/components/ui/Input.js';
import { Label } from '@/components/ui/Label.js';
import { Textarea } from '@/components/ui/Textarea.js';
import { api, ApiError, type UpdateSecretInput } from '@/api/client.js';
import { KindBadge } from './Secrets.js';

export function SecretDetailPage(): JSX.Element {
  const { id } = useParams({ strict: false }) as { id?: string };
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['secret', id],
    queryFn: () => api.secrets.get(id!),
    enabled: !!id,
  });
  const secret = query.data;

  const [editing, setEditing] = useState(false);
  const [name, setName] = useState('');
  const [metadataText, setMetadataText] = useState('{}');
  const [metadataError, setMetadataError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (!secret) return;
    setName(secret.name);
    setMetadataText(JSON.stringify(secret.metadata ?? {}, null, 2));
  }, [secret]);

  async function save() {
    if (!secret) return;
    setSaving(true);
    setSaveError(null);
    let parsedMetadata: Record<string, unknown>;
    try {
      parsedMetadata = metadataText.trim() ? JSON.parse(metadataText) : {};
      setMetadataError(null);
    } catch (err) {
      setMetadataError(err instanceof Error ? err.message : 'Invalid JSON');
      setSaving(false);
      return;
    }
    const patch: UpdateSecretInput = {};
    if (name !== secret.name) patch.name = name;
    patch.metadata = parsedMetadata;
    try {
      await api.secrets.update(secret.id, patch);
      toast.success('Saved');
      await queryClient.invalidateQueries({ queryKey: ['secret', secret.id] });
      await queryClient.invalidateQueries({ queryKey: ['secrets'] });
      setEditing(false);
    } catch (err) {
      setSaveError(err instanceof ApiError ? err.message : 'Could not save.');
    } finally {
      setSaving(false);
    }
  }

  async function destroy() {
    if (!secret) return;
    if (!confirm(`Delete ${secret.name}? This cannot be undone — anything referencing it (access methods, etc.) will break.`)) return;
    try {
      await api.secrets.delete(secret.id);
      toast.success(`Deleted ${secret.name}`);
      await queryClient.invalidateQueries({ queryKey: ['secrets'] });
      await navigate({ to: '/p/applications/secrets' });
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not delete.');
    }
  }

  if (query.isLoading) {
    return (
      <div className="flex justify-center py-12 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }
  if (!secret) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-sm">
          <KeyRound className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
          <p>Secret not found, or you don't have access to it.</p>
          <Button variant="link" onClick={() => navigate({ to: '/p/applications/secrets' })}>
            Back to secrets
          </Button>
        </CardContent>
      </Card>
    );
  }

  const kind = secret.metadata?.['kind'] as string | undefined;
  const publicKey = secret.metadata?.['publicKey'] as string | undefined;
  const fingerprint = secret.metadata?.['fingerprint'] as string | undefined;

  return (
    <div className="space-y-8">
      <div>
        <Link
          to="/p/applications/secrets"
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <ChevronLeft className="h-3 w-3" />
          All secrets
        </Link>
      </div>

      <header className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <KeyRound className="h-6 w-6" />
          </div>
          <div className="space-y-1">
            {editing ? (
              <Input value={name} onChange={(e) => setName(e.target.value)} className="text-xl font-semibold" />
            ) : (
              <h1 className="text-2xl font-semibold tracking-tight">{secret.name}</h1>
            )}
            <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
              <KindBadge kind={kind} />
              <Link
                to="/orgs/$slug"
                params={{ slug: secret.orgSlug }}
                className="inline-flex items-center gap-1 hover:text-foreground"
              >
                {secret.orgName}
              </Link>
              <span>·</span>
              <span className="font-mono">{secret.id}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {editing ? (
            <>
              <Button variant="ghost" onClick={() => setEditing(false)}>
                <X className="h-4 w-4" />
                Cancel
              </Button>
              <Button onClick={save} disabled={saving || !name}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Save
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => setEditing(true)}>
                <Pencil className="h-4 w-4" />
                Edit
              </Button>
              <Button variant="ghost" onClick={destroy}>
                <Trash2 className="h-4 w-4" />
                Delete
              </Button>
            </>
          )}
        </div>
      </header>

      {saveError && (
        <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {saveError}
        </p>
      )}

      {publicKey && (
        <Card>
          <CardHeader className="flex flex-row items-start justify-between space-y-0">
            <div>
              <CardTitle className="flex items-center gap-2">
                <ShieldCheck className="h-4 w-4" />
                Public key
              </CardTitle>
              <CardDescription>
                Paste this into <span className="font-mono">~/.ssh/authorized_keys</span> on the
                target server so DeployIt can connect.
              </CardDescription>
            </div>
            <Button
              variant="outline"
              onClick={() => {
                void navigator.clipboard.writeText(publicKey);
                toast.success('Public key copied');
              }}
            >
              <Copy className="h-3.5 w-3.5" />
              Copy
            </Button>
          </CardHeader>
          <CardContent>
            <pre className="overflow-x-auto break-all rounded-md border border-border bg-muted/30 p-3 font-mono text-[11px]">
              {publicKey}
            </pre>
            {fingerprint && (
              <p className="mt-2 text-[11px] text-muted-foreground">
                Fingerprint: <span className="font-mono">{fingerprint}</span>
              </p>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>About</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
            <Row label="Created" value={new Date(secret.createdAt).toLocaleString()} />
            <Row
              label="Last used"
              value={secret.lastUsedAt ? new Date(secret.lastUsedAt).toLocaleString() : 'never'}
            />
            {secret.applicationId && <Row label="Application scope" value={secret.applicationId} mono />}
            {secret.componentId && <Row label="Component scope" value={secret.componentId} mono />}
            {secret.pluginId && <Row label="Owning plugin" value={secret.pluginId} mono />}
          </dl>
          <p className="rounded-md border border-border bg-muted/30 px-3 py-2 text-[11px] text-muted-foreground">
            <Info className="mr-1 inline h-3 w-3" />
            The plaintext is encrypted at rest with AES-256-GCM and is never retrievable from
            this UI. The server reads it only when running an action that needs it (e.g. opening
            an SSH connection), and never holds it longer than the call.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Metadata</CardTitle>
          <CardDescription>
            Free-form JSON. Public-key + fingerprint live here for SSH secrets so any consumer
            can reference them without round-tripping the plaintext.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {editing ? (
            <Textarea
              value={metadataText}
              onChange={(e) => setMetadataText(e.target.value)}
              rows={8}
              className="font-mono text-[11px]"
            />
          ) : (
            <pre className="overflow-x-auto rounded-md border border-border bg-muted/30 p-3 font-mono text-[11px]">
              {JSON.stringify(secret.metadata ?? {}, null, 2)}
            </pre>
          )}
          {metadataError && <p className="text-xs text-destructive">{metadataError}</p>}
        </CardContent>
      </Card>
    </div>
  );
}

function Row({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className={mono ? 'break-all font-mono' : undefined}>{value}</dd>
    </>
  );
}
